import React, { useEffect, useState } from "react";
import PageTemplate from "../../../Templates/PageTemplate";
import { useNavigate } from "react-router";
import Cookies from 'universal-cookie';
import middleware from "../../../Api/Middleware";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { BackButton } from "../../../HouseStyle/Components/Buttons";
import { BorderedTextInput } from "../../../HouseStyle/Components/Inputs";
import { BorderedDropDown } from "../../../HouseStyle/Components/DropDowns";
import TextEditor from "../../../Main/TextEditor";
import { RegularCheckBox } from "../../../HouseStyle/Components/Checkboxes";
import ToastSuccess from "../../../Toasts/ToastSuccess";
import ToastError from "../../../Toasts/ToastError";
import ToastInfo from "../../../Toasts/ToastInfo";

const GeneralInfo = () => {
    const [loading, setLoading] = useState(true);
    const cookies = new Cookies();
    const navigate = useNavigate();
    const [companyInfo, setCompanyInfo] = useState({});
    const [companyInfoOld, setCompanyInfoOld] = useState({});
    const [mainDebounceTimer, setMainDebounceTimer] = useState(null);
    const [invoiceDebounceTimer, setInvoiceDebounceTimer] = useState(null);
    const [countryInfo, setCountryInfo] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState({});
    const [modals, setModals] = useState({
        deactivateCompanyInfo: false
    });
    const toggleModal = (modal) => setModals((prev) => ({ ...prev, [modal]: !prev[modal] }));
    const [isDifferentInvoiceAddress, setIsDifferentInvoiceAddress] = useState(false);
    const [streetNameEditable, setStreetNameEditable] = useState(false);
    const [cityEditable, setCityEditable] = useState(false);
    const [invoiceStreetNameEditable, setInvoiceStreetNameEditable] = useState(false);
    const [invoiceCityEditable, setInvoiceCityEditable] = useState(false);

    // Maak een pending state voor de invoer
    const [pendingCompanyInfo, setPendingCompanyInfo] = useState(companyInfo);
    const isNetherlands = selectedCountry.country_code_2 === "NL";
    const diffBillAddressValue = companyInfo.diff_bill_address ?? companyInfo.diffBillAddress ?? 0;
    useEffect(() => {
        setPendingCompanyInfo(companyInfo);
    }, [companyInfo]);

    const fetchCountry = async () => {
        const [fetchCountries] = await Promise.all([
            middleware.get(`/countryCodes`),
        ]);
        const sorted = fetchCountries.data.data.sort((a, b) => b.order_by - a.order_by);
        
        // Find the index of Germany (Duitsland) to insert a disabled separator after it
        const germanyIndex = sorted.findIndex(country => country.country_code_2 === "DE");
        
        if (germanyIndex !== -1) {
            // Insert a disabled separator after Germany
            sorted.splice(germanyIndex + 1, 0, {
                country_code_id: 'separator',
                country: '',
                country_code_2: null,
                disabled: true,
                order_by: sorted[germanyIndex].order_by - 0.5
            });
        }
        
        setCountryInfo(sorted);
    };

    const fetchCompanyInfo = async () => {
        const settings = await middleware.get("/settings/backoffice?setting_id=1");
        setCompanyInfo(settings.data.data);
        setCompanyInfoOld(settings.data.data);
        
        // Set selected country based on company info
        if (settings.data.data.country_code_id) {
            const country = countryInfo.find(c => c.country_code_id === settings.data.data.country_code_id);
            if (country) {
                setSelectedCountry(country);
            }
        }
        
        setLoading(false);
    };

    useEffect(() => {
        fetchCountry();
    }, []);

    useEffect(() => {
        if (countryInfo.length > 0) {
            fetchCompanyInfo();
        }
    }, [countryInfo]);

    useEffect(() => {
        // Vergelijk de nieuwe companyInfo met de oude. Als ze gelijk zijn, doe niets.
        if (JSON.stringify(companyInfo) === JSON.stringify(companyInfoOld)) return;
        
        UpdateCompanyInfo();
        // Werk companyInfoOld bij zodat volgende wijzigingen wel herkend worden.
        setCompanyInfoOld(companyInfo);
    }, [companyInfo]);

    useEffect(() => {
        if (companyInfo.diffBillAddress === 0) {
            setPendingCompanyInfo((prev) => ({
                ...prev,
                invoice_postalcode: prev.postal_code,
                invoice_house_number: prev.house_number,
                invoice_addition: prev.addition,
                invoice_streetname: prev.street_name,
                invoice_city: prev.city,
            }));
        }
    }, [companyInfo.diffBillAddress, companyInfo.postal_code, companyInfo.house_number, companyInfo.addition, companyInfo.street_name, companyInfo.city]);
    useEffect(() => {
        if (isNetherlands) {
            const { postal_code, house_number } = companyInfo;
            const postalCodeRegex = /^\s*[0-9]{4}\s*[A-Za-z]{2}\s*$/;

            if (mainDebounceTimer) {
                clearTimeout(mainDebounceTimer);
            }

            if (postal_code) {
                const newDebounceTimer = setTimeout(() => {
                    if (postalCodeRegex.test(postal_code)) {
                        const fetchAdress = async () => {
                            if (house_number) {
                                try {
                                    const response = await fetch(`https://gratispostcodeapi.nl/api/v1/lookup/${postal_code}/${house_number}`);
                                    const result = await response.json();
                                    if (result && result.postal_code) {
                                        setCompanyInfo(prev => {
                                            const suffixCandidate = result.available_suffixes && result.available_suffixes.length > 0
                                                ? result.available_suffixes[0]
                                                : undefined;
                                            const nextAddition = (!prev.addition || prev.addition === "") && suffixCandidate
                                                ? suffixCandidate
                                                : prev.addition;

                                            if (
                                                prev.street_name === result.street &&
                                                prev.city === result.city &&
                                                nextAddition === prev.addition
                                            ) {
                                                return prev;
                                            }

                                            return {
                                                ...prev,
                                                street_name: result.street,
                                                city: result.city,
                                                addition: nextAddition,
                                            };
                                        });
                                    } else {
                                        ToastError("Geen adres gevonden voor deze postcode.");
                                    }
                                } catch (error) {
                                    ToastError("Er is een fout opgetreden bij het ophalen de adress gegevens");
                                }
                            } else {
                                ToastInfo("Voer het huisnummer in om automatisch de straat en stad in te vullen");
                            }
                        };
                        fetchAdress();
                    }
                }, 1000);
                setMainDebounceTimer(newDebounceTimer);
            }

            return () => clearTimeout(mainDebounceTimer);
        }
    }, [companyInfo.country, companyInfo.postal_code, companyInfo.house_number, isNetherlands, companyInfo.addition]);

    useEffect(() => {
        if (isNetherlands && diffBillAddressValue === 1) {
            const { invoice_postalcode, invoice_house_number } = companyInfo;
            const postalCodeRegex = /^\s*[0-9]{4}\s*[A-Za-z]{2}\s*$/;

            if (invoiceDebounceTimer) {
                clearTimeout(invoiceDebounceTimer);
            }

            if (invoice_postalcode) {
                const newDebounceTimer = setTimeout(() => {
                    if (postalCodeRegex.test(invoice_postalcode)) {
                        const fetchAdress = async () => {
                            if (invoice_house_number) {
                                try {
                                    const response = await fetch(`https://gratispostcodeapi.nl/api/v1/lookup/${invoice_postalcode}/${invoice_house_number}`);
                                    const result = await response.json();
                                    if (result && result.postal_code) {
                                        setCompanyInfo(prev => {
                                            const suffixCandidate = result.available_suffixes && result.available_suffixes.length > 0
                                                ? result.available_suffixes[0]
                                                : undefined;
                                            const nextAddition = (!prev.invoice_addition || prev.invoice_addition === "") && suffixCandidate
                                                ? suffixCandidate
                                                : prev.invoice_addition;

                                            if (
                                                prev.invoice_streetname === result.street &&
                                                prev.invoice_city === result.city &&
                                                nextAddition === prev.invoice_addition
                                            ) {
                                                return prev;
                                            }

                                            return {
                                                ...prev,
                                                invoice_streetname: result.street,
                                                invoice_city: result.city,
                                                invoice_addition: nextAddition,
                                            };
                                        });
                                    } else {
                                        ToastError("Geen adres gevonden voor deze postcode.");
                                    }
                                } catch (error) {
                                    ToastError("Er is een fout opgetreden bij het ophalen de adress gegevens");
                                }
                            } else {
                                ToastInfo("Voer het huisnummer in om automatisch de straat en stad in te vullen");
                            }
                        };
                        fetchAdress();
                    }
                }, 1000);
                setInvoiceDebounceTimer(newDebounceTimer);
            }

            return () => clearTimeout(invoiceDebounceTimer);
        }
    }, [companyInfo.invoice_postalcode, companyInfo.invoice_house_number, companyInfo.invoice_addition, diffBillAddressValue, isNetherlands]);

    const UpdateCompanyInfo = async () => {
        try {
          await middleware.put("/settings/backoffice", {
            setting_id: 1,
            B2B_B2C: companyInfo.B2B_B2C,
            addition: companyInfo.addition,
            city: companyInfo.city,
            cocnumber: companyInfo.cocnumber,
            company_name: companyInfo.company_name,
            country: companyInfo.country,
            country_code_id: companyInfo.country_code_id,
            email: companyInfo.email,
            house_number: companyInfo.house_number,
            diff_bill_address: companyInfo.diff_bill_address,
            invoice_addition: companyInfo.invoice_addition,
            invoice_postalcode: companyInfo.invoice_postalcode,
            invoice_city: companyInfo.invoice_city,
            invoice_house_number: companyInfo.invoice_house_number,
            invoice_streetname: companyInfo.invoice_streetname,
            phone_number: companyInfo.phone_number,
            postal_code: companyInfo.postal_code,
            street_name: companyInfo.street_name,
            vatnumber: companyInfo.vatnumber,
            website: companyInfo.website,
          });
          ToastSuccess('Company information updated successfully');
        } catch (error) {
          console.error('Error updating company information:', error);
        }
    };
    
    return (
        <PageTemplate pageIsLoading={loading} navbarTitle={"Bedrijsinformatie"}>
            <div className="col-12 d-flex flex-column">
                <div className="d-flex mb-4">
                    <BackButton size="l" text={"Terug"} onClickFunction={() => navigate("/algemene/instellingen/bedrijfsinformatie")} />
                </div>
                <div className="whiteBox px-5 py-4">
                    <h2 className="fw-semibold">Bedrijfsinformatie aanpassen</h2>
                    <h5 className="fw-bold pt-3">Jouw bedrijf</h5>
                    <div className="row">
                        <div className="col-4">
                            <h6 className="fw-bold py-2 pt-4">Bedrijfsnaam *</h6>
                            <BorderedTextInput 
                                placeholder="e.g. 0000"
                                size="l"
                                inputValue={pendingCompanyInfo.company_name || ""}
                                setInputValue={(value) =>
                                    setPendingCompanyInfo((prev) => ({ ...prev, company_name: value }))
                                }
                                updateFunction={() =>
                                    setCompanyInfo((prev) => ({ ...prev, company_name: pendingCompanyInfo.company_name }))
                                }
                                updateWaitTimer={1000}
                            />
                        </div>
                        <div className="col-2">
                            <h6 className="fw-bold py-2 pt-4">KvK nummer</h6>
                            <BorderedTextInput 
                                placeholder="e.g. 0000"
                                size="l"
                                inputValue={pendingCompanyInfo.cocnumber || ""}
                                setInputValue={(value) =>
                                    setPendingCompanyInfo((prev) => ({ ...prev, cocnumber: value }))
                                }
                                updateFunction={() =>
                                    setCompanyInfo((prev) => ({ ...prev, cocnumber: pendingCompanyInfo.cocnumber }))
                                }
                                updateWaitTimer={1000}
                            />
                        </div>
                        {/* <div className="col-2 d-flex align-items-end">
                            <BorderedDropDown 
                                placeholder="e.g. Nederland" 
                                selectedValue={companyInfo.country || ""} 
                                setSelectedValue={(e) => setCompanyInfo((prev) => ({ ...prev, country: e }))}
                                options={[
                                    { title: "Nederland", value: "Nederland" }, 
                                    { title: "Duitsland", value: "Duitsland" }
                                ]} 
                            />
                        </div> */}
                        <div className="col-6"></div>
                        <div className="col-2">
                            <h6 className="fw-bold py-2 pt-4">E-mailadres</h6>
                            <BorderedTextInput 
                                placeholder="email"
                                size="l"
                                inputValue={pendingCompanyInfo.email || ""}
                                setInputValue={(value) =>
                                    setPendingCompanyInfo((prev) => ({ ...prev, email: value }))
                                }
                                updateFunction={() =>
                                    setCompanyInfo((prev) => ({ ...prev, email: pendingCompanyInfo.email }))
                                }
                                updateWaitTimer={1000}
                            />
                        </div>
                        <div className="col-2">
                            <h6 className="fw-bold py-2 pt-4">Telefoon</h6>
                            <BorderedTextInput 
                                placeholder="e.g. 0000"
                                size="l"
                                inputValue={pendingCompanyInfo.phone_number || ""}
                                setInputValue={(value) =>
                                    setPendingCompanyInfo((prev) => ({ ...prev, phone_number: value }))
                                }
                                updateFunction={() =>
                                    setCompanyInfo((prev) => ({ ...prev, phone_number: pendingCompanyInfo.phone_number }))
                                }
                                updateWaitTimer={1000}
                            />
                        </div>
                        <div className="col-2">
                            <h6 className="fw-bold py-2 pt-4">Website</h6>
                            <BorderedTextInput 
                                placeholder="e.g. 0000"
                                size="l"
                                inputValue={pendingCompanyInfo.website || ""}
                                setInputValue={(value) =>
                                    setPendingCompanyInfo((prev) => ({ ...prev, website: value }))
                                }
                                updateFunction={() =>
                                    setCompanyInfo((prev) => ({ ...prev, website: pendingCompanyInfo.website }))
                                }
                                updateWaitTimer={1000}
                            />
                        </div>
                    </div>
                </div>
                <div className="whiteBox px-5 py-4 mt-3">
                    <div className="row">
                        <div className="col-4 row">
                            <h6 className="fw-bold py-2 pt-4">Land</h6>
                            <BorderedDropDown
                                placeholder="Kies een land"
                                selectedValue={selectedCountry.country_code_id || ''}
                                setSelectedValue={(value) => {
                                    const country = countryInfo.find(country => country.country_code_id === value);
                                    setSelectedCountry(country);
                                    setCompanyInfo((prev) => ({ 
                                        ...prev, 
                                        country_code_id: value,
                                        country: country?.country 
                                    }));
                                }}
                                size="l"
                                options={
                                    countryInfo?.length > 0 &&
                                    countryInfo.map((country) => {
                                        return {
                                            key: country.order_by,
                                            title: country.country,
                                            value: country.country_code_id,
                                            disabled: country.disabled
                                        };
                                    })
                                }
                            />
                        </div>
                        <div className="col-2"></div>
                        <div className="col-6 row">
                            <RegularCheckBox
                                value={pendingCompanyInfo.diff_bill_address === 1 ? true : false} 
                                setValue={(value) => setCompanyInfo((prev) => ({ ...prev, diff_bill_address: value === true ? 1 : 0 }))} 
                                label={"Afwijkend factuuradres"} 
                            />
                        </div>
                        <div className="col-12"><br/></div>
                        <div className="col-4 row">
                            <h5 className="fw-bold pt-3">Hoofdadres</h5>
                            <div className="col-4">
                                <h6 className="fw-bold py-2 pt-4">Postcode</h6>
                                <BorderedTextInput 
                                    placeholder="e.g. 0000"
                                    inputValue={pendingCompanyInfo.postal_code || ""}
                                    setInputValue={(value) =>
                                        setPendingCompanyInfo((prev) => ({ ...prev, postal_code: value }))
                                    }
                                    updateFunction={() =>
                                        setCompanyInfo((prev) => ({ ...prev, postal_code: pendingCompanyInfo.postal_code }))
                                    }
                                    updateWaitTimer={1000}
                                    disabled={!selectedCountry.country_code_2}
                                />
                            </div>
                            <div className="col-4">
                                <h6 className="fw-bold py-2 pt-4">Huisnummer</h6>
                                <BorderedTextInput 
                                    placeholder="e.g. 0000"
                                    inputValue={pendingCompanyInfo.house_number || ""}
                                    setInputValue={(value) =>
                                        setPendingCompanyInfo((prev) => ({ ...prev, house_number: value }))
                                    }
                                    updateFunction={() =>
                                        setCompanyInfo((prev) => ({ ...prev, house_number: pendingCompanyInfo.house_number }))
                                    }
                                    updateWaitTimer={1000}
                                    disabled={!selectedCountry.country_code_2}
                                />
                            </div>
                            <div className="col-4">
                                <h6 className="fw-bold py-2 pt-4">Toevoeging</h6>
                                <BorderedTextInput 
                                    size="regular"
                                    placeholder="e.g. 0000"
                                    inputValue={pendingCompanyInfo.addition || ""}
                                    setInputValue={(value) =>
                                        setPendingCompanyInfo((prev) => ({ ...prev, addition: value }))
                                    }
                                    updateFunction={() =>
                                        setCompanyInfo((prev) => ({ ...prev, addition: pendingCompanyInfo.addition }))
                                    }
                                    updateWaitTimer={1000}
                                    disabled={!selectedCountry.country_code_2}
                                />
                            </div>
                            <div className="col-12">
                                <h6 className="fw-bold py-2 pt-4">
                                    Adres
                                    {selectedCountry.country_code_2 === "NL" && pendingCompanyInfo.street_name && (
                                        <FontAwesomeIcon
                                            style={{ marginLeft: "5px", cursor: "pointer" }}
                                            onClick={() => setStreetNameEditable(!streetNameEditable)}
                                            icon={faPencilAlt}
                                        />
                                    )}
                                </h6>
                                <BorderedTextInput 
                                    size="regular"
                                    placeholder="e.g. 0000"
                                    inputValue={pendingCompanyInfo.street_name || ""}
                                    setInputValue={(value) =>
                                        setPendingCompanyInfo((prev) => ({ ...prev, street_name: value }))
                                    }
                                    updateFunction={() =>
                                        setCompanyInfo((prev) => ({ ...prev, street_name: pendingCompanyInfo.street_name }))
                                    }
                                    updateWaitTimer={1000}
                                    disabled={!selectedCountry.country_code_2 || (!streetNameEditable && selectedCountry.country_code_2 === "NL")}
                                />
                                <h6 className="fw-bold py-2 pt-4">
                                    Stad
                                    {selectedCountry.country_code_2 === "NL" && pendingCompanyInfo.city && (
                                        <FontAwesomeIcon
                                            style={{ marginLeft: "5px", cursor: "pointer" }}
                                            onClick={() => setCityEditable(!cityEditable)}
                                            icon={faPencilAlt}
                                        />
                                    )}
                                </h6>
                                <BorderedTextInput 
                                    size="regular"
                                    placeholder="e.g. 0000"
                                    inputValue={pendingCompanyInfo.city || ""}
                                    setInputValue={(value) =>
                                        setPendingCompanyInfo((prev) => ({ ...prev, city: value }))
                                    }
                                    updateFunction={() =>
                                        setCompanyInfo((prev) => ({ ...prev, city: pendingCompanyInfo.city }))
                                    }
                                    updateWaitTimer={1000}
                                    disabled={!selectedCountry.country_code_2 || (!cityEditable && selectedCountry.country_code_2 === "NL")}
                                />
                            </div>
                        </div>
                        <div className="col-2"></div>
                        <div className="col-4 row">
                            {companyInfo.diff_bill_address === 1 && (
                                <>
                                    <h5 className="fw-bold pt-2">Factuuradres</h5>
                                    <div className="col-4">
                                        <h6 className="fw-bold py-2 pt-4">Postcode</h6>
                                        <BorderedTextInput 
                                            size="regular"
                                            placeholder="e.g. 0000"
                                            inputValue={pendingCompanyInfo.invoice_postalcode || ""}
                                            setInputValue={(value) =>
                                                setPendingCompanyInfo((prev) => ({ ...prev, invoice_postalcode: value }))
                                            }
                                            updateFunction={() =>
                                                setCompanyInfo((prev) => ({ ...prev, invoice_postalcode: pendingCompanyInfo.invoice_postalcode }))
                                            }
                                            updateWaitTimer={1000}
                                            disabled={!selectedCountry.country_code_2 || companyInfo.diffBillAddress === 0}
                                        />
                                    </div>
                                    <div className="col-4">
                                        <h6 className="fw-bold py-2 pt-4">Huisnummer</h6>
                                        <BorderedTextInput 
                                            size="regular"
                                            placeholder="e.g. 0000"
                                            inputValue={pendingCompanyInfo.invoice_house_number || ""}
                                            setInputValue={(value) =>
                                                setPendingCompanyInfo((prev) => ({ ...prev, invoice_house_number: value }))
                                            }
                                            updateFunction={() =>
                                                setCompanyInfo((prev) => ({ ...prev, invoice_house_number: pendingCompanyInfo.invoice_house_number }))
                                            }
                                            updateWaitTimer={1000}
                                            disabled={!selectedCountry.country_code_2 || companyInfo.diffBillAddress === 0}
                                        />
                                    </div>
                                    <div className="col-4">
                                        <h6 className="fw-bold py-2 pt-4">Toevoeging</h6>
                                        <BorderedTextInput 
                                            size="regular"
                                            placeholder="e.g. 0000"
                                            inputValue={pendingCompanyInfo.invoice_addition || ""}
                                            setInputValue={(value) =>
                                                setPendingCompanyInfo((prev) => ({ ...prev, invoice_addition: value }))
                                            }
                                            updateFunction={() =>
                                                setCompanyInfo((prev) => ({ ...prev, invoice_addition: pendingCompanyInfo.invoice_addition }))
                                            }
                                            updateWaitTimer={1000}
                                            disabled={!selectedCountry.country_code_2 || companyInfo.diffBillAddress === 0}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <h6 className="fw-bold py-2 pt-4">
                                            Adres
                                            {selectedCountry.country_code_2 === "NL" && pendingCompanyInfo.invoice_streetname && (
                                                <FontAwesomeIcon
                                                    style={{ marginLeft: "5px", cursor: "pointer" }}
                                                    onClick={() => setInvoiceStreetNameEditable(!invoiceStreetNameEditable)}
                                                    icon={faPencilAlt}
                                                />
                                            )}
                                        </h6>
                                        <BorderedTextInput 
                                            size="regular"
                                            placeholder="e.g. 0000"
                                            inputValue={pendingCompanyInfo.invoice_streetname || ""}
                                            setInputValue={(value) =>
                                                setPendingCompanyInfo((prev) => ({ ...prev, invoice_streetname: value }))
                                            }
                                            updateFunction={() =>
                                                setCompanyInfo((prev) => ({ ...prev, invoice_streetname: pendingCompanyInfo.invoice_streetname }))
                                            }
                                            updateWaitTimer={1000}
                                            disabled={!selectedCountry.country_code_2 || (!invoiceStreetNameEditable && selectedCountry.country_code_2 === "NL") || companyInfo.diffBillAddress === 0}
                                        />
                                        <h6 className="fw-bold py-2 pt-4">
                                            Stad
                                            {selectedCountry.country_code_2 === "NL" && pendingCompanyInfo.invoice_city && (
                                                <FontAwesomeIcon
                                                    style={{ marginLeft: "5px", cursor: "pointer" }}
                                                    onClick={() => setInvoiceCityEditable(!invoiceCityEditable)}
                                                    icon={faPencilAlt}
                                                />
                                            )}
                                        </h6>
                                        <BorderedTextInput 
                                            size="regular"
                                            placeholder="e.g. 0000"
                                            inputValue={pendingCompanyInfo.invoice_city || ""}
                                            setInputValue={(value) =>
                                                setPendingCompanyInfo((prev) => ({ ...prev, invoice_city: value }))
                                            }
                                            updateFunction={() =>
                                                setCompanyInfo((prev) => ({ ...prev, invoice_city: pendingCompanyInfo.invoice_city }))
                                            }
                                            updateWaitTimer={1000}
                                            disabled={!selectedCountry.country_code_2 || (!invoiceCityEditable && selectedCountry.country_code_2 === "NL") || companyInfo.diffBillAddress === 0}
                                        />
                                    </div>
                                </> 
                            )}
                        </div>
                    </div>
                </div>
                <div className="whiteBox px-5 py-4 mt-3">
                    <div className="row">
                        <div className="col-7">
                            Bij het instellen van uw UnifiedSMB omgeving kunt u kiezen tussen B2C (Business-to-Consumer) en B2B
                            (Business-to-Business). Deze keuze bepaalt hoe het systeem omgaat met BTW-berekeningen, afhankelijk van uw
                            bedrijfsmodel:
                            <br /><br />
                            <span className="fw-bold">B2C:</span> Het systeem werkt met prijzen inclusief BTW op basis van het BTW-tarief van het land waarin
                            uw bedrijf gevestigd is. Ideaal voor bedrijven die direct aan consumenten verkopen.
                            <br /><br />
                            <span className="fw-bold">B2B:</span> Het systeem werkt met prijzen exclusief BTW en geeft u de mogelijkheid om BTW-gegevens
                            handmatig in te voeren. Perfect voor zakelijke transacties en verkoop aan bedrijven.
                            <br /><br />
                            Kies de optie die het beste aansluit bij uw bedrijfsactiviteiten, zodat het systeem optimaal afgestemd is
                            op uw behoeften.
                        </div>
                        <div className="col d-flex justify-content-center align-items-center">
                            <div className="d-flex flex-column">
                                <h6 className="fw-bold text-center">Selecteer de hoofddoelgroep</h6>
                                <BorderedDropDown 
                                    size="s" 
                                    selectedValue={companyInfo.b2b_b2c} 
                                    setSelectedValue={(e) => setCompanyInfo((prev) => ({ ...prev, b2b_b2c: e }))} 
                                    options={[
                                        { title: "B2C", value: 1 }, 
                                        { title: "B2B", value: 0, disabled: true }
                                    ]} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageTemplate>
    );
};

export default GeneralInfo;
